import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import { FormattedNumber, useIntl } from "react-intl";
import moment from "moment";
import ICatalog_Item from "@models/ICatalog_Item";
import { useAppContextNonNull } from "@contexts/AppContext";
import { IInProductionStepDetail } from "@models/DTO/IOnGetInProductionBreakdownDTO";
import IOnGetInProductionAllocationDTO, { IWaitingOrderLine } from "@models/DTO/IOnGetInProductionAllocationDTO";
import IBulkAssignSerialsDTO from "@models/DTO/IBulkAssignSerialsDTO";
import IOnBulkAssignSerialsDTO from "@models/DTO/IOnBulkAssignSerialsDTO";
import InventoryService from "@services/InventoryService";
import SerialService from "@services/SerialService";
import OwButton from "@components/shared/OwButton/OwButton";
import { FinishedSkuPicker } from "@components/inventory/Serials/FinishedSkuPicker/FinishedSkuPicker";
import "./InventoryItemInProductionDetailSidebar.scss";

type Props = {
    catalogItem: ICatalog_Item;
    closeFn: () => void;
};

export const InventoryItemInProductionDetailSidebar = (props: Props) => {
    const { catalogItem, closeFn } = props;
    const { userModel, supplierModel } = useAppContextNonNull();
    const intl = useIntl();

    const [steps, setSteps] = useState<IInProductionStepDetail[]>([]);
    const [totalInProduction, setTotalInProduction] = useState(0);
    const [catalogItemSku, setCatalogItemSku] = useState<string | undefined>();
    const [catalogItemName, setCatalogItemName] = useState<string | undefined>();
    const [isLoading, setIsLoading] = useState(true);
    const [allocation, setAllocation] = useState<IOnGetInProductionAllocationDTO | null>(null);
    const [expandedStepIds, setExpandedStepIds] = useState<Set<number>>(new Set());
    const [selectedSerialIds, setSelectedSerialIds] = useState<Set<number>>(new Set());
    const [selectedOrderItemGuid, setSelectedOrderItemGuid] = useState<string | null>(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [serialErrors, setSerialErrors] = useState<string[]>([]);

    // Assign to a different finished SKU (re-SKU on assign, OR-1459): a BOM v2 surface.
    const canReclassify = !!supplierModel?.has_bom_v2;
    const [showReclassify, setShowReclassify] = useState(false);
    const [targetItem, setTargetItem] = useState<ICatalog_Item | null>(null);
    const [targetWaitingOrders, setTargetWaitingOrders] = useState<IWaitingOrderLine[]>([]);
    const [isLoadingTargetOrders, setIsLoadingTargetOrders] = useState(false);
    const [targetOrderItemGuid, setTargetOrderItemGuid] = useState<string | null>(null);
    // Latest finished-SKU pick: a slower response for an earlier pick (or one that lands after
    // Cancel) must not install that SKU's lines under the current pick — the API re-SKUs to the
    // line's item, so a stale line would classify the serials as the wrong SKU.
    const targetRequestId = useRef(0);

    const fetchData = useCallback(async () => {
        setIsLoading(true);

        try {
            const [breakdownResult, allocationResult] = await Promise.all([
                InventoryService.getInProductionBreakdown(catalogItem.guid, userModel, supplierModel),
                InventoryService.getInProductionAllocation(catalogItem.guid, userModel, supplierModel),
            ]);

            if (breakdownResult.success && breakdownResult.data) {
                setSteps(breakdownResult.data.steps);
                setTotalInProduction(breakdownResult.data.totalInProduction);
                setCatalogItemSku(breakdownResult.data.catalogItemSku);
                setCatalogItemName(breakdownResult.data.catalogItemName);
            }

            if (allocationResult.success && allocationResult.data) {
                setAllocation(allocationResult.data);
                // Preselect the oldest waiting order (server-sorted) so a
                // received unit can be earmarked with a single click.
                setSelectedOrderItemGuid((prev) => {
                    const stillWaiting = allocationResult.data!.waitingOrders.some((line) => line.orderItemGuid === prev);
                    return stillWaiting ? prev : (allocationResult.data!.waitingOrders[0]?.orderItemGuid ?? null);
                });
            }
        } catch (error) {
            console.error("Error fetching in-production details:", error);
        } finally {
            setIsLoading(false);
        }
    }, [catalogItem.guid, userModel, supplierModel]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const toggleStepExpanded = (stepId: number) => {
        setExpandedStepIds((prev) => {
            const next = new Set(prev);
            if (next.has(stepId)) {
                next.delete(stepId);
            } else {
                next.add(stepId);
            }
            return next;
        });
    };

    const toggleSerialSelected = (serialId: number) => {
        setSelectedSerialIds((prev) => {
            const next = new Set(prev);
            if (next.has(serialId)) {
                next.delete(serialId);
            } else {
                next.add(serialId);
            }
            return next;
        });
    };

    const selectedLine: IWaitingOrderLine | undefined = allocation?.waitingOrders.find(
        (line) => line.orderItemGuid === selectedOrderItemGuid
    );
    const overNeeded = !!selectedLine && selectedSerialIds.size > selectedLine.needed;
    const canAssign = selectedSerialIds.size > 0 && !!selectedOrderItemGuid && !overNeeded && !isAssigning;

    /** Per-serial failures from bulk-assign, labelled with the serial number when we have it. */
    const describeSerialErrors = (result: IOnBulkAssignSerialsDTO): string[] =>
        result.errors.map((error) => {
            const serial = allocation?.serials.find((candidate) => candidate.id === error.serialId);
            return `${serial?.value || error.serialId}: ${error.reason}`;
        });

    const handleAssign = async () => {
        if (!canAssign || !selectedOrderItemGuid) return;

        try {
            setIsAssigning(true);
            setErrorMessage("");
            setSerialErrors([]);

            const dto: IBulkAssignSerialsDTO = {
                orderItemGuid: selectedOrderItemGuid,
                serialIds: Array.from(selectedSerialIds),
            };

            const result = await SerialService.bulkAssignSerials(dto, userModel, supplierModel);
            setSerialErrors(describeSerialErrors(result));
            setSelectedSerialIds(new Set());
            await fetchData();
        } catch (error: any) {
            console.error("Error assigning serials:", error);
            setErrorMessage(error.message || "Error assigning serials");
        } finally {
            setIsAssigning(false);
        }
    };

    const selectTargetItem = async (item: ICatalog_Item | null) => {
        const requestId = ++targetRequestId.current;

        setTargetItem(item);
        setTargetWaitingOrders([]);
        setTargetOrderItemGuid(null);

        if (!item) {
            setIsLoadingTargetOrders(false);
            return;
        }

        try {
            setIsLoadingTargetOrders(true);
            const result = await InventoryService.getInProductionAllocation(item.guid, userModel, supplierModel);

            if (requestId !== targetRequestId.current) return;

            const waitingOrders = result.success && result.data ? result.data.waitingOrders : [];
            setTargetWaitingOrders(waitingOrders);
            setTargetOrderItemGuid(waitingOrders[0]?.orderItemGuid ?? null);
        } catch (error) {
            if (requestId === targetRequestId.current) {
                console.error("Error fetching waiting orders for the finished SKU:", error);
            }
        } finally {
            if (requestId === targetRequestId.current) {
                setIsLoadingTargetOrders(false);
            }
        }
    };

    const closeReclassify = () => {
        // Drop any waiting-orders load still in flight.
        targetRequestId.current++;
        setIsLoadingTargetOrders(false);
        setShowReclassify(false);
        setTargetItem(null);
        setTargetWaitingOrders([]);
        setTargetOrderItemGuid(null);
    };

    const targetLine: IWaitingOrderLine | undefined = targetWaitingOrders.find((line) => line.orderItemGuid === targetOrderItemGuid);
    const targetOverNeeded = !!targetLine && selectedSerialIds.size > targetLine.needed;
    const canReclassifyAssign =
        selectedSerialIds.size > 0 && !!targetItem && !!targetOrderItemGuid && !targetOverNeeded && !isLoadingTargetOrders && !isAssigning;

    const handleReclassifyAssign = async () => {
        if (!canReclassifyAssign || !targetOrderItemGuid) return;

        try {
            setIsAssigning(true);
            setErrorMessage("");
            setSerialErrors([]);

            const dto: IBulkAssignSerialsDTO = {
                orderItemGuid: targetOrderItemGuid,
                serialIds: Array.from(selectedSerialIds),
                reclassify: true,
            };

            const result = await SerialService.bulkAssignSerials(dto, userModel, supplierModel);
            setSerialErrors(describeSerialErrors(result));
            setSelectedSerialIds(new Set());

            if (!result.errors.length) {
                closeReclassify();
            }

            await fetchData();
        } catch (error: any) {
            console.error("Error classifying serials:", error);
            setErrorMessage(error.message || "Error classifying serials");
        } finally {
            setIsAssigning(false);
        }
    };

    const renderWaitingOrderLabel = (line: IWaitingOrderLine) => (
        <>
            <strong>{line.orderNo}</strong>
            {line.customerName && <span> - {line.customerName}</span>}
            <span className="text-muted small ms-1">
                ({moment(line.orderDate).format("l")}, {line.orderState})
            </span>
            <span className="ms-1">Needed: {line.needed}</span>
            {line.assignedWip > 0 && <span className="text-muted small ms-1">({line.assignedWip} in production assigned)</span>}
        </>
    );

    const renderReclassifySection = () => {
        if (!showReclassify) {
            return (
                <OwButton
                    title={intl.formatMessage({
                        id: "assign_to_different_finished_sku",
                        defaultMessage: "Assign to a different finished SKU",
                    })}
                    className="btn-outline-primary btn-sm mt-2"
                    skipTranslate={true}
                    fn={() => setShowReclassify(true)}
                />
            );
        }

        return (
            <div className="reclassify-section mt-3" data-testid="reclassify-section">
                <h6>
                    {intl.formatMessage({ id: "assign_to_different_finished_sku", defaultMessage: "Assign to a different finished SKU" })}
                </h6>
                <FinishedSkuPicker
                    defaultCatalogId={catalogItem.catalog_id}
                    excludeCatalogItemId={catalogItem.id}
                    selectedItem={targetItem}
                    onSelect={selectTargetItem}
                />
                {targetItem && (
                    <div className="mt-2">
                        {isLoadingTargetOrders ? (
                            <i className="fas fa-spinner fa-spin"></i>
                        ) : targetWaitingOrders.length === 0 ? (
                            <div className="text-muted">
                                {intl.formatMessage({
                                    id: "no_waiting_orders_for_finished_sku",
                                    defaultMessage: "No waiting orders for this finished SKU.",
                                })}
                            </div>
                        ) : (
                            <div className="waiting-orders-box rounded p-1">
                                {targetWaitingOrders.map((line) => (
                                    <div key={line.orderItemGuid} className="form-check">
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            name="targetWaitingOrder"
                                            id={`target-waiting-order-${line.orderItemGuid}`}
                                            checked={targetOrderItemGuid === line.orderItemGuid}
                                            onChange={() => setTargetOrderItemGuid(line.orderItemGuid)}
                                        />
                                        <label className="form-check-label" htmlFor={`target-waiting-order-${line.orderItemGuid}`}>
                                            {renderWaitingOrderLabel(line)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
                {targetOverNeeded && (
                    <div className="text-danger small mt-1">Selection exceeds the quantity needed by the selected order.</div>
                )}
                {targetItem && targetLine && selectedSerialIds.size > 0 && (
                    <div className="fw-bold mt-2" data-testid="reclassify-confirm-text">
                        {intl.formatMessage(
                            { id: "classify_n_serials_as_sku", defaultMessage: "Classify {count} serials as {sku}?" },
                            { count: selectedSerialIds.size, sku: targetItem.sku }
                        )}
                    </div>
                )}
                <div className="d-flex gap-2 mt-2">
                    <OwButton
                        title={intl.formatMessage({ id: "classify_and_assign", defaultMessage: "Classify and Assign" })}
                        className="btn-success"
                        skipTranslate={true}
                        fn={handleReclassifyAssign}
                        disabled={!canReclassifyAssign}
                    />
                    <OwButton
                        title={intl.formatMessage({ id: "cancel", defaultMessage: "Cancel" })}
                        className="btn-danger"
                        skipTranslate={true}
                        fn={closeReclassify}
                    />
                </div>
            </div>
        );
    };

    const renderStepRow = (step: IInProductionStepDetail) => {
        const stepSerials = (allocation?.serials || []).filter((serial) => serial.stepId === step.stepId);
        const isExpanded = expandedStepIds.has(step.stepId);
        const earmarkedCount = step.serialCount - stepSerials.length;

        return (
            <Fragment key={step.stepId}>
                <tr className={stepSerials.length ? "expandable-step" : undefined}>
                    <td onClick={stepSerials.length ? () => toggleStepExpanded(step.stepId) : undefined}>
                        {stepSerials.length ? <i className={`fas fa-chevron-${isExpanded ? "down" : "right"} me-2`} /> : null}
                        {step.stepName}
                    </td>
                    <td className="text-end">
                        <FormattedNumber value={step.serialCount} minimumFractionDigits={0} />
                        {earmarkedCount > 0 && <span className="text-muted small ms-1">({earmarkedCount} assigned)</span>}
                    </td>
                </tr>
                {isExpanded &&
                    stepSerials.map((serial) => (
                        <tr key={`serial-${serial.id}`} className="serial-row">
                            <td colSpan={2}>
                                <div className="form-check ms-4">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id={`wip-serial-${serial.id}`}
                                        checked={selectedSerialIds.has(serial.id)}
                                        onChange={() => toggleSerialSelected(serial.id)}
                                    />
                                    <label className="form-check-label" htmlFor={`wip-serial-${serial.id}`}>
                                        {serial.value || serial.id}
                                    </label>
                                </div>
                            </td>
                        </tr>
                    ))}
            </Fragment>
        );
    };

    return (
        <div className="InventoryItemInProductionDetailSidebar">
            <div className="sidebar-header">
                <div className="header-content">
                    <h5 className="sidebar-title">In Production Details</h5>
                    <div className="item-info">
                        <strong>{catalogItemSku || catalogItem.sku}</strong>
                        {(catalogItemName || catalogItem.description) && <span> - {catalogItemName || catalogItem.description}</span>}
                    </div>
                    <div className="summary-badge">
                        Total In Production: <FormattedNumber value={totalInProduction} minimumFractionDigits={0} />
                    </div>
                </div>
                <OwButton centerIconClass="fas fa-times" className="btn-danger" fn={closeFn} />
            </div>

            <div className="table-container">
                {isLoading ? (
                    <div className="loading-container">
                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                    </div>
                ) : steps.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-industry fa-3x mb-3"></i>
                        <p>No serials are in production for this item.</p>
                    </div>
                ) : (
                    <table className="steps-table">
                        <thead>
                            <tr>
                                <th>Step</th>
                                <th className="text-end">Serials</th>
                            </tr>
                        </thead>
                        <tbody>{steps.map((step) => renderStepRow(step))}</tbody>
                    </table>
                )}
            </div>

            {!isLoading && steps.length > 0 && (
                <div className="allocation-section">
                    <h6 className="mt-3">Waiting Orders</h6>
                    {!allocation || allocation.waitingOrders.length === 0 ? (
                        <div className="text-muted">No waiting orders for this item.</div>
                    ) : (
                        <>
                            <div className="waiting-orders-box rounded p-1">
                                {allocation.waitingOrders.map((line) => (
                                    <div key={line.orderItemGuid} className="form-check">
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            name="waitingOrder"
                                            id={`waiting-order-${line.orderItemGuid}`}
                                            checked={selectedOrderItemGuid === line.orderItemGuid}
                                            onChange={() => setSelectedOrderItemGuid(line.orderItemGuid)}
                                        />
                                        <label className="form-check-label" htmlFor={`waiting-order-${line.orderItemGuid}`}>
                                            {renderWaitingOrderLabel(line)}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            {overNeeded && (
                                <div className="text-danger small mt-1">Selection exceeds the quantity needed by the selected order.</div>
                            )}
                            <OwButton
                                title="Assign To Order"
                                className="btn-success mt-2"
                                skipTranslate={true}
                                fn={handleAssign}
                                disabled={!canAssign}
                            />
                        </>
                    )}
                    {canReclassify && renderReclassifySection()}
                    {errorMessage && <div className="text-danger small mt-1">{errorMessage}</div>}
                    {serialErrors.length > 0 && (
                        <ul className="text-danger small mt-1 mb-0 ps-3" data-testid="serial-assign-errors">
                            {serialErrors.map((message) => (
                                <li key={message}>{message}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};
